import convert from 'convert-units';
import _ from 'lodash';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { handleRequestFileUpload } from '@/shared/api';
import { UTC_US_DAYLIGHTT_TIME, UTC_US_STANDARD_TIME, CHARACTER_PER_LINE } from '@/shared/config';
import auth from './auth/auth';

export { default as auth } from './auth/auth';
export { default as getAccessToken } from './auth/authToken';
export { checkAuthMode, switchAmplifyAuthMode, getAwsConfigByMode } from './auth/amplifyAuthMode';
export { default as updatedAwsConfig } from './auth/amplifyAuthMode';
export { default as isEnvLocalDev } from './browser/isEnvLocalDev';
export { default as getTabId } from './getTabId';

export * from './persistence/dbMetaData';
export * from './persistence/dbHistoryState';
export * from './persistence/dbState';
export * from './persistence/dbBeatData';
export * from './persistence/dbECGData';
export * from './persistence/undoRedoState';
export * from './persistence/reportPersistence';
export { enableReportPersistence, disableReportPersistence } from './persistence/createPerStudyDb';
export * from './persistence/dbCleanup';
export * from './persistence/activeTabs';
export * from './parsePageInput';

// TODO(ts): typed as the facility record once Apollo domains land
type Facility = any;

/**
 * Trims zero padding out of a timezone label for display. Strips a plus-zero-zero suffix when present, otherwise removes every zero character, and returns an empty string when no timezone is given.
 *
 * @param timezone - Timezone label such as UTC+00.
 * @returns The compacted timezone label.
 */
const removeZeroTimezone = (timezone?: string) => {
  if (!timezone) {
    return '';
  }
  if (timezone.includes('+00')) {
    return timezone.replaceAll('+00', '');
  }
  if (timezone.includes('0')) {
    return timezone.replaceAll('0', '');
  }
  return timezone;
};

export const formatTimezone = ({
  startStudyDate,
  isoDate,
  timezone,
  facility,
}: {
  startStudyDate?: moment.MomentInput;
  isoDate?: moment.MomentInput;
  timezone?: string;
  facility?: Facility;
}) => {
  if (!facility || facility.contact?.country !== 'US' || !isoDate) {
    return removeZeroTimezone(timezone);
  }

  if (startStudyDate) {
    const isStartStudyDateDST = momentTimezone.tz(startStudyDate, facility.timezone).isDST();
    const isIsoDateDST = momentTimezone.tz(isoDate, facility.timezone).isDST();
    let newTimezone: number | string = _.toNumber(_.split(timezone, 'UTC')[1]);
    if (!isStartStudyDateDST && isIsoDateDST) {
      newTimezone += 1;
    }
    if (isStartStudyDateDST && !isIsoDateDST) {
      newTimezone -= 1;
    }
    newTimezone = `UTC${newTimezone > 0 ? '+' : ''}${newTimezone}`;
    const utc = _.find(
      isIsoDateDST ? UTC_US_DAYLIGHTT_TIME : UTC_US_STANDARD_TIME,
      (x) => x.utc === newTimezone,
    );
    if (utc) {
      return removeZeroTimezone(utc.name);
    }
  }

  const isDST = momentTimezone.tz(isoDate, facility.timezone).isDST();
  const utc = _.find(
    isDST ? UTC_US_DAYLIGHTT_TIME : UTC_US_STANDARD_TIME,
    (x) => x.utc === timezone,
  );
  if (utc) {
    return removeZeroTimezone(utc.name);
  }

  return removeZeroTimezone(timezone);
};

/**
 * Converts a UTC-prefixed timezone label into an offset in minutes. Parses the hour count following the first three characters and multiplies by 60, yielding zero if parsing throws.
 *
 * @param timezone - Timezone label such as UTC+7.
 * @returns The offset in minutes, or zero when the label cannot be parsed.
 */
export const timezoneToOffset = (timezone: string) => {
  try {
    const timeString = timezone.substring(3);
    return parseInt(timeString, 10) * 60;
  } catch (exception) {
    return 0;
  }
};

const getUtcOffsetWithDst = ({
  startStudyDate,
  isoDate,
  timezone,
  facilityCountry,
  facilityTimezone = 'America/New_York',
}: {
  startStudyDate?: moment.MomentInput;
  isoDate?: moment.MomentInput;
  timezone: string;
  facilityCountry?: string;
  facilityTimezone?: string;
}) => {
  let utcOffset = timezoneToOffset(timezone);
  let newTimezone = _.toNumber(_.split(timezone, 'UTC')[1]);
  if (startStudyDate && facilityCountry === 'US') {
    const isStartStudyDateDST = momentTimezone.tz(startStudyDate, facilityTimezone).isDST();
    const isIsoDateDST = momentTimezone.tz(isoDate, facilityTimezone).isDST();
    if (!isStartStudyDateDST && isIsoDateDST) {
      // *: From standard to DST
      utcOffset += 60;
      newTimezone += 1;
    }
    if (isStartStudyDateDST && !isIsoDateDST) {
      // *: From DST to standard
      utcOffset -= 60;
      newTimezone -= 1;
    }
    // *: 2 dates are in the same time zone range or facility is in time zone that do not observe DST, do nothing
  }
  return {
    utcOffset,
    newTimezone: `UTC${newTimezone > 0 ? '+' : ''}${newTimezone}`,
  };
};

export const dateToStringWithDailyTimeZone = ({
  startStudyDate,
  isoDate,
  timezone = 'UTC+0',
  facility,
  format,
}: {
  startStudyDate?: moment.MomentInput;
  isoDate?: moment.MomentInput;
  timezone?: string;
  facility?: Facility;
  format?: string;
}) => {
  if (isoDate) {
    const { utcOffset, newTimezone } = getUtcOffsetWithDst({
      startStudyDate,
      isoDate,
      timezone,
      facilityCountry: facility?.contact?.country,
      facilityTimezone: facility?.timezone,
    });
    const dateMoment = moment(isoDate).utcOffset(utcOffset);
    return dateMoment.isValid()
      ? format
        ? dateMoment.format(format)
        : `${dateMoment.format('MM/DD/YYYY, HH:mm:ss')} ${formatTimezone({
            startStudyDate,
            isoDate,
            timezone: newTimezone,
            facility,
          })}`
      : '';
  }
  return '';
};

/**
 * Formats a timestamp for display in the study's timezone. Applies the timezone label as a minute offset and formats with the given pattern, which defaults to including the offset for call-center users, returning an empty string for a missing or invalid date.
 *
 * @param isoDate - Timestamp to format.
 * @param timezone - Timezone label; defaults to UTC+0.
 * @param format - Moment format pattern; defaults to a call-center aware pattern.
 * @returns The formatted date string, or an empty string.
 */
export const dateToString = (
  isoDate: moment.MomentInput,
  timezone = 'UTC+0',
  format = auth.isCallCenter() ? 'MM/DD/YYYY, HH:mm:ss Z' : 'MM/DD/YYYY, HH:mm:ss',
) => {
  if (isoDate) {
    const utcOffset = timezoneToOffset(timezone);
    const dateMoment = moment(isoDate).utcOffset(utcOffset);
    return dateMoment.isValid() ? dateMoment.format(format) : '';
  }
  return '';
};

/**
 * Left-pads a number with zeros for fixed-width identifiers. Pads to the requested width, treating zero as a real value and returning an empty string for any other falsy input.
 *
 * @param num - Number to pad.
 * @param places - Target width; defaults to 5.
 * @returns The zero-padded string, or an empty string when there is no number.
 */
export const zeroPad = (num: number, places = 5) => {
  if (num || num === 0) {
    return num.toString().padStart(places, '0');
  }
  return '';
};

/**
 * Measures a study's recording span in whole minutes. Subtracts the timestamps and rounds the millisecond difference to minutes, yielding zero when either bound is missing.
 *
 * @param start - Study start timestamp.
 * @param stop - Study stop timestamp.
 * @returns The study duration in minutes, or zero when a bound is missing.
 */
export const getStudyDuration = (start: string | number | Date, stop: string | number | Date) => {
  if (start && stop) {
    const duration = new Date(stop).getTime() - new Date(start).getTime();
    return Math.round(duration / 1000 / 60);
  }
  return 0;
};

/**
 * Uploads one file to a pre-signed URL. Decodes a signature payload out of its base64 data URL prefix and sets a PNG content type for it, then issues the PUT, logging and yielding undefined if preparing the request throws.
 *
 * @param url - Pre-signed destination URL.
 * @param binaryData - Payload to upload.
 * @param isSignature - Whether the payload is a base64 signature image.
 * @returns A promise resolving to the PUT response, or undefined when preparing the request threw.
 */
export const putFileToUrl = (url: string, binaryData: any, isSignature: boolean) => {
  try {
    const uploadData = isSignature
      ? Buffer.from(_.replace(binaryData, 'data:image/png;base64,', ''), 'base64')
      : binaryData;
    const options = {
      method: 'PUT',
      body: uploadData,
    };
    if (isSignature) {
      _.assign(options, {
        headers: { 'Content-Type': 'image/png; charset=utf-8' },
      });
    }
    return fetch(url, options);
  } catch (error) {
    console.error('Failed to put file to url:', error);
    return undefined;
  }
};

/**
 * Uploads one or more files by first requesting pre-signed URLs for them. Asks for as many URLs as there are payload entries, PUTs each entry in parallel, and hands back the single URL or the whole list, logging and rethrowing on failure and yielding null when no URLs were issued.
 *
 * @param binaryData - Payload entries to upload.
 * @param type - Kind of file being uploaded.
 * @param isSignature - Whether the payloads are base64 signature images; defaults to false.
 * @returns A promise resolving to the upload URL, the list of URLs, or null when none were issued.
 */
export const uploadFileToUrl = async (binaryData: any, type: string, isSignature = false) => {
  try {
    const uploadFileUrls = await handleRequestFileUpload(type, binaryData.length);
    if (uploadFileUrls?.length) {
      const putFileToUrlPromises = _.map(uploadFileUrls, (uploadFileUrl, index) =>
        putFileToUrl(uploadFileUrl, binaryData[index], isSignature),
      );
      await Promise.all(putFileToUrlPromises);
      return uploadFileUrls.length === 1 ? uploadFileUrls[0] : uploadFileUrls;
    }
    return null;
  } catch (error) {
    console.log('Failed to upload file: ', error);
    throw error;
  }
};

/**
 * Reads an ISO timestamp in a study's timezone label. Converts the label to a minute offset and applies it to the parsed moment, yielding null for a missing or invalid date.
 *
 * @param isoDate - Timestamp to convert.
 * @param timezone - Timezone label; defaults to UTC+0.
 * @returns A moment at the requested offset, or null.
 */
export const convertToMoment = (isoDate: moment.MomentInput, timezone = 'UTC+0') => {
  if (isoDate) {
    const utcOffset = timezoneToOffset(timezone);
    const dateMoment = moment(isoDate);
    dateMoment.utcOffset(utcOffset);
    return dateMoment.isValid() ? dateMoment : null;
  }
  return null;
};

/**
 * Reads an ISO timestamp shifted the opposite way from its timezone label, so UTC-relative values line up. Converts the label to a minute offset and applies its negation to the parsed moment, yielding null for a missing or invalid date.
 *
 * @param isoDate - Timestamp to convert.
 * @param timezone - Timezone label; defaults to UTC+0.
 * @returns A moment at the negated offset, or null.
 */
export const convertToMomentWithUTC0 = (isoDate: moment.MomentInput, timezone = 'UTC+0') => {
  if (isoDate) {
    const utcOffset = timezoneToOffset(timezone);
    const dateMoment = moment(isoDate).utcOffset(-utcOffset);
    return dateMoment.isValid() ? dateMoment : null;
  }
  return null;
};

/**
 * Formats a timestamp into the compact stamp used inside report file names. Applies the timezone label as a minute offset and formats as YYYYMMDD_HHmm, returning an empty string for a missing or invalid date.
 *
 * @param isoDate - Timestamp to format.
 * @param timezone - Timezone label; defaults to UTC+0.
 * @returns The formatted stamp, or an empty string.
 */
export const dataToStringForNameReport = (isoDate: moment.MomentInput, timezone = 'UTC+0') => {
  if (isoDate) {
    const utcOffset = timezoneToOffset(timezone);
    const dateMoment = moment(isoDate).utcOffset(utcOffset);
    return dateMoment.isValid() ? dateMoment.format('YYYYMMDD_HHmm') : '';
  }
  return '';
};

/**
 * Splits a patient height given in decimal feet into feet and inches. Truncates the whole feet and converts the fractional part into rounded inches, yielding an empty object when there are no whole feet.
 *
 * @param decimalHeight - Height in decimal feet.
 * @returns An object carrying ft and ins, or an empty object when there are no whole feet.
 */
export const convertDecimalHeightToFeetInch = (decimalHeight: number) => {
  const newFt = parseInt(String(decimalHeight), 10);
  const newIns = convert(decimalHeight - newFt)
    .from('ft')
    .to('in');
  if (newFt) {
    return {
      ft: newFt,
      ins: Math.round(newIns),
    };
  }
  return {};
};

/**
 * Counts how many rendered lines a block of report text will occupy. Splits on line breaks, drops the empty lines, and adds the extra wrapped lines for any line longer than the per-line character budget.
 *
 * @param value - Text to measure.
 * @returns The number of rendered lines.
 */
export const getLineParagraph = (value: string) => {
  let lineCount = 0;
  const splitedLineBreakValue = value.split(/\r\n|\r|\n/);
  _.remove(splitedLineBreakValue, (x) => !x); // *: Remove empty lines
  lineCount += splitedLineBreakValue.length; // *: Line count included all line breaks
  _.forEach(splitedLineBreakValue, (splitedLine) => {
    const singleLineCount = Math.ceil(splitedLine.length / CHARACTER_PER_LINE);
    if (singleLineCount > 1) {
      // *: Add more line count if each single line has larger than 1 line
      lineCount += singleLineCount - 1;
    }
  });
  return lineCount;
};

/**
 * Collapses blank lines out of a multi-line comment or note. Splits on line breaks, drops the empty entries and rejoins with newlines, returning an empty string for a falsy input.
 *
 * @param value - Text to clean.
 * @returns The text with its blank lines removed.
 */
export const removeEmptyLineBreak = (value: string) => {
  if (!value) return '';
  const splitedLineBreakValue = value.split(/\r\n|\r|\n/);
  _.remove(splitedLineBreakValue, (x) => !x);
  return splitedLineBreakValue.join('\n');
};

/**
 * Reports whether a device has passed its three-year service life. Compares the given date against now in whole years and returns true at three or more, and false when no date is given.
 *
 * @param date - Manufacture or activation date to age.
 * @returns True when the date is at least three years old.
 */
export const isEndOfLife = (date: moment.MomentInput) => {
  if (date) {
    const timeDiff = moment().diff(moment(date), 'year');
    return timeDiff >= 3;
  }

  return false;
};

export const FACILITY_NOTES_MAX_CHAR = 2000;

/**
 * Inverts a lookup table so its values become the keys. Reduces the entries into a new object, with later duplicate values overwriting earlier ones.
 *
 * @param obj - Table to invert.
 * @returns The inverted object.
 */
export const flipObject = (obj: Record<string, string | number>) =>
  Object.entries(obj).reduce((ret: Record<string, string>, entry) => {
    const [key, value] = entry;
    ret[value] = key;
    return ret;
  }, {});

/**
 * Checks that a string parses as an http or https URL. Attempts URL construction and returns false when it throws, otherwise accepting only the http and https protocols.
 *
 * @param string - Candidate URL text.
 * @returns True when the string is a valid http or https URL.
 */
export const isValidHttpUrl = (string: string) => {
  let url;
  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

/**
 * Dispatches a synthetic keydown so keyboard shortcuts can be fired from the UI. Builds a bubbling cancelable KeyboardEvent carrying the key, its derived code and char code plus the shift, ctrl and meta flags, then dispatches it on the document.
 *
 * @param key - Key character to simulate.
 * @param modifiers - Optional shiftKey, ctrlKey and metaKey flags.
 * @returns Nothing.
 */
export const simulateKeyEvent = (
  key: string,
  modifiers: { shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean } = {},
) => {
  const event = new KeyboardEvent('keydown', {
    bubbles: true,
    cancelable: true,
    key,
    code: `Key${key.toUpperCase()}`,
    keyCode: key.charCodeAt(0),
    charCode: 0,
    shiftKey: modifiers.shiftKey || false,
    ctrlKey: modifiers.ctrlKey || false,
    metaKey: modifiers.metaKey || false,
  });

  document.dispatchEvent(event);
};

// Defined in its own module so per-study db modules can import it without pulling the whole
// @/shared/lib barrel (which would create an import cycle).
export { splitAndConvert } from './splitAndConvert';

/**
 * Derives a stable numeric hash from a string. Runs the classic multiply-by-31 loop over the character codes, coercing to a 32-bit integer at each step, and returns zero for an empty string.
 *
 * @param string - Text to hash.
 * @returns A 32-bit integer hash of the string.
 */
export const hashStringCode = (string: string) => {
  let hash = 0;
  let i;
  let chr;
  if (string.length === 0) return hash;
  for (i = 0; i < string.length; i += 1) {
    chr = string.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
};

/**
 * Compares two beat type codes, treating the two encodings of a normal beat as equal. Returns true for identical codes and for the 1 and 90 pair that both denote an N beat.
 *
 * @param current - Beat type code being checked.
 * @param prev - Beat type code to compare against.
 * @returns True when both codes denote the same beat type.
 */
export const isSameBeats = (current: number, prev: number) => {
  if (current === prev) return true;
  if ((current === 90 && prev === 1) || (current === 1 && prev === 90)) return true; // N beat has 2 values are 1 and 90
  return current === prev;
};

/**
 * Compares two dot-separated version strings against a minimum requirement. Pads the shorter version with zeros, compares part by part numerically, and treats a missing version on either side as failing outright.
 *
 * @param currentVersion - Version being checked.
 * @param targetVersion - Minimum version required.
 * @returns True when the current version is at least the target.
 */
export const isVersionAtLeast = (
  currentVersion: string | number,
  targetVersion: string | number,
) => {
  if (!currentVersion || !targetVersion) {
    return false;
  }
  const current = String(currentVersion).split('.');
  const target = String(targetVersion).split('.');
  const maxLength = Math.max(current.length, target.length);

  for (let i = 0; i < maxLength; i += 1) {
    const currentPart = Number.parseInt(current[i] ?? '0', 10) || 0;
    const targetPart = Number.parseInt(target[i] ?? '0', 10) || 0;

    if (currentPart !== targetPart) {
      return currentPart > targetPart;
    }
  }

  return true;
};
